import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations, Language } from '../data/locales';

interface I18nContextType {
  language: Language;
  t: (key: string) => string;
  changeLanguage: (lang: Language) => void;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('it'); // Start with default, will update on mount

  // Helper to get nested properties from translations object (e.g. 'nav.home')
  const t = (path: string): string => {
    const keys = path.split('.');
    let current: any = translations[language];
    
    for (const key of keys) {
      if (current[key] === undefined) {
        console.warn(`Translation key missing: ${path} for language ${language}`);
        return path;
      }
      current = current[key];
    }
    return current;
  };

  useEffect(() => {
    // 1. Check Cookie
    const getCookie = (name: string) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(';').shift();
    };

    const cookieLang = getCookie('user_lang');

    // 2. Check Navigator
    const getBrowserLang = (): Language => {
      const browserLang = navigator.language.split('-')[0];
      if (['de', 'it', 'en'].includes(browserLang)) {
        return browserLang as Language;
      }
      return 'it'; // 3. Default Fallback
    };

    const detectedLang = (cookieLang as Language) || getBrowserLang();
    setLanguage(detectedLang);
  }, []);

  // Update HTML lang attribute for SEO
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    // Set Cookie: 30 days, SameSite=Lax
    const date = new Date();
    date.setTime(date.getTime() + (30 * 24 * 60 * 60 * 1000));
    document.cookie = `user_lang=${lang}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
  };

  return (
    <I18nContext.Provider value={{ language, t, changeLanguage }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
};